/* eslint-disable no-magic-numbers */
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { getOrdinalSuffix } from '@futrli/localisation/core/utils';

import { Recurrence, RecurrenceEndType, RecurrenceWeekDay, THE_LAST } from './types';

const WEEK_DAY_MESSAGE_IDS: Record<RecurrenceWeekDay, { id: string; defaultMessage: string }> = {
  [RecurrenceWeekDay.MONDAY]: { id: 'common.monday', defaultMessage: 'Monday' },
  [RecurrenceWeekDay.TUESDAY]: { id: 'common.tuesday', defaultMessage: 'Tuesday' },
  [RecurrenceWeekDay.WEDNESDAY]: { id: 'common.wednesday', defaultMessage: 'Wednesday' },
  [RecurrenceWeekDay.THURSDAY]: { id: 'common.thursday', defaultMessage: 'Thursday' },
  [RecurrenceWeekDay.FRIDAY]: { id: 'common.friday', defaultMessage: 'Friday' },
  [RecurrenceWeekDay.SATURDAY]: { id: 'common.saturday', defaultMessage: 'Saturday' },
  [RecurrenceWeekDay.SUNDAY]: { id: 'common.sunday', defaultMessage: 'Sunday' }
};

export const RepeatMessage = ({
  recurrence,
  repeatValue,
  weekDay,
  monthDay
}: {
  recurrence: Recurrence;
  repeatValue: string;
  weekDay: RecurrenceWeekDay;
  monthDay: string;
}) => {
  const intl = useIntl();

  if (recurrence === Recurrence.DAILY) {
    return (
      <FormattedMessage
        id="predict.schedule_summary.repeat_daily"
        defaultMessage="Every {count} day(s)"
        values={{ count: repeatValue }}
      />
    );
  }

  if (recurrence === Recurrence.WEEKLY) {
    const dayName = intl.formatMessage(WEEK_DAY_MESSAGE_IDS[weekDay]);
    return (
      <FormattedMessage
        id="predict.schedule_summary.repeat_weekly"
        defaultMessage="Every {count} week(s) on {day}"
        values={{ count: repeatValue, day: dayName }}
      />
    );
  }

  if (recurrence === Recurrence.MONTHLY) {
    const dayText =
      monthDay === THE_LAST
        ? intl.formatMessage({ id: 'predict.schedule_summary.the_last_day', defaultMessage: 'the last day of the month' })
        : intl.formatMessage(
            { id: 'predict.schedule_summary.nth_of_the_month', defaultMessage: 'the {ordinal} of the month' },
            { ordinal: getOrdinalSuffix(Number(monthDay), intl.locale) }
          );

    return (
      <FormattedMessage
        id="predict.schedule_summary.repeat_monthly"
        defaultMessage="Every {count} month(s) on {dayText}"
        values={{ count: repeatValue, dayText }}
      />
    );
  }

  return null;
};

export const EndsMessage = ({
  endType,
  occurancesValue,
  endDate
}: {
  endType: RecurrenceEndType;
  occurancesValue: string;
  endDate: Date;
}) => {
  const intl = useIntl();

  if (endType === RecurrenceEndType.AFTER_N_OCCURANCES) {
    return (
      <FormattedMessage
        id="predict.schedule_summary.ends_after"
        defaultMessage="After {count} occurrence(s)"
        values={{ count: occurancesValue }}
      />
    );
  }

  if (endType === RecurrenceEndType.ON_THIS_DAY) {
    return <>{intl.formatDate(endDate, { day: 'numeric', month: 'long', year: 'numeric' })}</>;
  }

  return <FormattedMessage id="common.never" defaultMessage="Never" />;
};
